import { useCallback, useEffect, useMemo } from 'react'
import {
  IconHashtag,
  IconBuildings,
  IconPlus,
  IconCreditCard,
  Badge,
  Button,
  Skeleton,
  GreyGrey60,
  MagicTableCell,
  MagicTableCellEmpty,
  MagicTableFilterConfig,
  MagicTable,
  useMagicTableInfiniteQuery,
  useLinkMagicTableWithSearchParams,
  magicTableToApiQueryParams,
  MagicTableEmptyState,
  MagicTableFilterEmptyState,
  Box,
  Flex,
  ActiveFilter
} from '@sequencehq/core-components'
import { dateTimeWithFormat } from '@sequencehq/utils'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import { customerLabelFilterConfig } from 'Customer/customerMagicTableFilter'
import {
  Customer,
  CustomersFilters,
  CustomersView
} from 'modules/Customers/types'
import { CustomerRowKebabMenu } from 'modules/Customers/components/CustomerRowKebabMenu'
import { getCustomerRole } from 'modules/Customers/utils/getCustomerRole'
import { TableLinksLogos } from 'modules/Integrations'
import { useFetchIntegrations } from 'Customer/hooks/useFetchIntegrations'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { taxStatusOptions } from 'modules/Customers/data'
import { useNavigate } from 'react-router-dom'
import { ProtectedButton } from 'lib/rbac'
import { countriesAlpha2, CountriesAlpha2 } from '@sequencehq/api'
import {
  useCustomerCustomPropertyFilters,
  useCustomerCustomPropertyObjectPropertyColumns
} from 'modules/Customers/hooks/useCustomerCustomPropertyColumns'
import { useForcedActiveFilters } from 'SavedViews/hooks/useForcedActiveFilters'
import { useIsOnSavedViewPage } from 'SavedViews/savedViews.router'
import { useSetAtom } from 'jotai'
import { customersFiltersApiQueryParamsAtom } from 'modules/Customers/customers.atoms'
import { getBillingAddress } from '@sequencehq/api/utils/commonModels'

const tableFilters: MagicTableFilterConfig<CustomersFilters>[] = [
  {
    type: 'text',
    paramName: 'legalName',
    label: 'Name',
    icon: IconBuildings
  },
  {
    type: 'text',
    paramName: 'alias',
    label: 'Alias',
    icon: IconHashtag
  },
  customerLabelFilterConfig,
  {
    type: 'multiSelect',
    paramName: 'billingStatus',
    label: 'Billing status',
    options: [
      { value: 'ACTIVE', label: 'Active' },
      { value: 'INACTIVE', label: 'Inactive' }
    ],
    format: value => (value === 'ACTIVE' ? 'Active' : 'Inactive'),
    icon: IconCreditCard,
    searchable: false
  },
  {
    paramName: 'includeArchived',
    type: 'toggle',
    label: 'Show archived customers'
  }
]

const noForcedActiveFilters: ActiveFilter[] = []

type Props = {
  onSaveFilters?: (activeFilters: ActiveFilter[], sortBy: string | null) => void
  onUpdateSavedFilters?: (
    activeFilters: ActiveFilter[],
    sortBy: string | null
  ) => void
  forcedActiveFilters?: ActiveFilter[]
  lockedControls?: boolean
}

export const CustomersMagicTable = ({
  onSaveFilters,
  onUpdateSavedFilters,
  forcedActiveFilters = noForcedActiveFilters,
  lockedControls = false
}: Props) => {
  const navigate = useNavigate()
  const isSavedView = useIsOnSavedViewPage()

  const { allCustomPropertyKeys, customPropertyFilters } =
    useCustomerCustomPropertyFilters(forcedActiveFilters)

  const filters = useMemo<MagicTableFilterConfig<CustomersFilters>[]>(
    () =>
      [...tableFilters, ...customPropertyFilters].map(filter => ({
        ...filter,
        disabled: forcedActiveFilters.some(
          forcedFilter => forcedFilter.paramName === filter.paramName
        )
      })),
    [customPropertyFilters, forcedActiveFilters]
  )

  const {
    activeFilters: searchActiveFilters,
    onChangeActiveFilters: onChangeSearchActiveFilters,
    sortBy,
    onChangeSortBy
  } = useLinkMagicTableWithSearchParams(filters)

  const { activeFilters, onChangeActiveFilters } = useForcedActiveFilters({
    forcedActiveFilters,
    searchActiveFilters,
    onChangeSearchActiveFilters
  })

  const setFiltersApiQueryParams = useSetAtom(
    customersFiltersApiQueryParamsAtom
  )
  useEffect(() => {
    setFiltersApiQueryParams(
      magicTableToApiQueryParams(filters, activeFilters, null)
    )
  }, [filters, activeFilters, setFiltersApiQueryParams])

  const handleSaveFilters = useCallback(() => {
    onSaveFilters?.(activeFilters, sortBy)
  }, [onSaveFilters, activeFilters, sortBy])

  const handleUpdateSavedFilters = useCallback(() => {
    onUpdateSavedFilters?.(activeFilters, sortBy)
  }, [onUpdateSavedFilters, activeFilters, sortBy])

  const { infiniteQuery } = useMagicTableInfiniteQuery(
    dashboardv99990101Client.getCustomers,
    filters,
    activeFilters,
    sortBy
  )

  const customPropertyObjectPropertyColumns =
    useCustomerCustomPropertyObjectPropertyColumns({
      allCustomPropertyKeys,
      activeFilters
    })

  const { useNetsuite } = useFeatureFlags()
  const { connectedIntegrations } = useFetchIntegrations()

  const visibleIntegrations = connectedIntegrations?.filter(integration => {
    if (integration.service === 'NetSuite' && !useNetsuite) return false
    return true
  })

  const integrationsLoaded = Boolean(connectedIntegrations)

  return (
    <CurrentUserId>
      {userId => (
        <MagicTable<CustomersView>
          key={integrationsLoaded ? 'loaded' : 'loading'}
          activeFilters={activeFilters}
          onChangeActiveFilters={onChangeActiveFilters}
          sortBy={sortBy}
          onChangeSortBy={onChangeSortBy}
          onSaveFilters={onSaveFilters ? handleSaveFilters : undefined}
          onUpdateSavedFilters={
            onUpdateSavedFilters ? handleUpdateSavedFilters : undefined
          }
          lockedControls={lockedControls}
          infiniteQuery={infiniteQuery}
          objectPropertyColumns={customPropertyObjectPropertyColumns}
          entityNamePlural="customers"
          sequenceUserId={userId}
          rowPath={(row: CustomersView['model']) => `/customers/${row.id}`}
          columns={[
            {
              id: 'legalName',
              header: 'Name',
              accessorFn: row => row,
              cell: value => {
                const customer = value.getValue<Customer>()

                const { organizations } = customer

                const customerRole = getCustomerRole({
                  organizations,
                  customerId: customer.id
                })

                return (
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    height="40px"
                    pl="6px"
                    flex="1"
                    overflow="hidden"
                    data-testid={`customers.${customer.id}.name`}
                  >
                    <Box minW="30px">
                      <CustomerPreviewCardPill
                        customer={customer}
                        variant="ghost"
                        data-testid={`customers.${customer.id}.previewPill`}
                      />
                    </Box>
                    {customerRole && (
                      <Box flex={1}>
                        <Badge sentiment="neutral" size="sm">
                          {customerRole}
                        </Badge>
                      </Box>
                    )}
                  </Flex>
                )
              },
              enableSorting: true
            },
            {
              id: 'addressCountry',
              header: 'Country',
              accessorFn: (row: CustomersView['model']) => ({
                customerId: row.id,
                countryCode: getBillingAddress(row.addresses)?.country,
                archivedAt: row.archivedAt
              }),
              cell: row => {
                const { customerId, countryCode, archivedAt } = row.getValue<{
                  customerId: CustomersView['model']['id']
                  countryCode: CountriesAlpha2 | undefined
                  archivedAt: CustomersView['model']['archivedAt']
                }>()

                return (
                  <MagicTableCell
                    // `MagicTableCell` renders nothing at all for empty text,
                    // so an address-less customer has no country cell to find
                    // rather than one holding a placeholder.
                    data-testid={`customers.${customerId}.country`}
                    textColor={archivedAt && GreyGrey60}
                    text={
                      countryCode
                        ? countriesAlpha2[countryCode] || countryCode
                        : undefined
                    }
                  />
                )
              },
              enableSorting: true
            },
            {
              id: 'createdAt',
              header: 'Created',
              accessorFn: (row: CustomersView['model']) => ({
                createdAt: row.createdAt,
                archivedAt: row.archivedAt
              }),
              cell: row => {
                const { createdAt, archivedAt } = row.getValue<{
                  createdAt: CustomersView['model']['createdAt']
                  archivedAt: CustomersView['model']['archivedAt']
                }>()

                return (
                  <MagicTableCell
                    textColor={archivedAt && GreyGrey60}
                    text={dateTimeWithFormat(createdAt, 'd MMM yyyy')}
                  />
                )
              },
              enableSorting: true
            },
            {
              id: 'integrationIds',
              header: 'Links',
              accessorKey: 'integrationIds',
              cell: row => {
                if (!connectedIntegrations) {
                  return (
                    <MagicTableCellEmpty>
                      <Skeleton height="24px" borderRadius="12px" />
                    </MagicTableCellEmpty>
                  )
                }

                const integrationIds =
                  row.getValue<CustomersView['model']['integrationIds']>()

                if (!integrationIds?.length) {
                  return null
                }

                return (
                  <MagicTableCellEmpty>
                    <TableLinksLogos
                      services={integrationIds.map(i => i.service)}
                    />
                  </MagicTableCellEmpty>
                )
              }
            },
            {
              id: 'label',
              header: 'Label',
              accessorKey: 'label',
              cell: row => (
                <MagicTableCell
                  text={row.getValue<CustomersView['model']['label']>()}
                />
              ),
              enableSorting: false,
              hiddenByDefault: true
            },
            {
              id: 'taxStatus',
              header: 'Tax status',
              accessorKey: 'taxStatus',
              cell: row => (
                <MagicTableCell
                  text={
                    taxStatusOptions[
                      row.getValue<CustomersView['model']['taxStatus']>()
                    ]
                  }
                />
              ),
              enableSorting: false
            }
          ]}
          filters={filters}
          emptyStateUnfiltered={entityNamePlural => (
            <MagicTableEmptyState
              data-testid="customers.emptyState"
              entityNamePlural={entityNamePlural}
              imageSrc="/img/empty/customers.png"
              learnMoreText="Create your first customer to send quotes, invoices, and automate billing"
              learnMoreHref="https://docs.sequencehq.com/customers/create-customer"
              buttonNew={
                <ProtectedButton permission="org.customer:create">
                  <Button
                    variant="primary"
                    leadingIcon={<IconPlus color="inherit" />}
                    onClick={() => navigate('/customers/create')}
                  >
                    New customer
                  </Button>
                </ProtectedButton>
              }
            />
          )}
          emptyStateFiltered={(entityNamePlural, clearFilters) => (
            <MagicTableFilterEmptyState
              entityNamePlural={entityNamePlural}
              entityIcon={IconBuildings}
              actionClearFilters={clearFilters}
              actionViewAll={() => navigate('/customers')}
              variant={isSavedView ? 'SAVED_VIEW' : 'TABLE'}
            />
          )}
          kebabMenu={(model: CustomersView['model'], props) => (
            <CustomerRowKebabMenu
              model={model}
              connectedIntegrations={visibleIntegrations}
              {...props}
            />
          )}
        />
      )}
    </CurrentUserId>
  )
}
